import { computed, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from '@bizzdesign/auth';
import {
  FilterGlobalStore,
  SearchResultsGlobalStore,
} from '@bizzdesign/data-access-search';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
  withHooks,
} from '@ngrx/signals';
import { tap } from 'rxjs';
import { generateUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';

/**
 * State backing the AI overview card and its feedback controls.
 *
 * @property commentBoxOpen - Whether the comment box under a thumbs-down is showing.
 * @property feedbackComment - What the user has typed into that comment box.
 * @property feedbackScore - The rating given to this answer: 1 for thumbs up, 0 for
 * thumbs down, `null` while unrated. Marks the chosen thumb as selected.
 * @property collapsed - Whether the card is collapsed to its compact pill.
 * @property authUserId - The signed-in user, attached to feedback the card emits.
 * `undefined` until the session resolves, and feedback is not emitted before then.
 * @property uniqueScoreId - Identity of the score this answer's rating is filed
 * under. Shared by the rating and any comment on it, and replaced when a new
 * answer arrives so two answers never file under one score.
 */
export type NlsOverviewAndFeedbackState = {
  commentBoxOpen: boolean;
  feedbackComment: string;
  feedbackScore: number | null;
  collapsed: boolean;
  authUserId: UUID | undefined;
  uniqueScoreId: UUID;
};

export const initialNlsOverviewAndFeedbackState: Omit<
  NlsOverviewAndFeedbackState,
  'uniqueScoreId'
> = {
  commentBoxOpen: false,
  feedbackComment: '',
  feedbackScore: null,
  collapsed: false,
  authUserId: undefined,
};

export const NlsOverviewAndFeedbackComponentStore = signalStore(
  // A factory, so every store instance gets its own id. A plain object would
  // call generateUUID() once at module load and share it across instances.
  withState(() => ({
    ...initialNlsOverviewAndFeedbackState,
    uniqueScoreId: generateUUID(),
  })),
  withProps(() => ({
    filterGlobalStore: inject(FilterGlobalStore),
    searchResultsGlobalStore: inject(SearchResultsGlobalStore),
  })),
  withComputed((store) => ({
    totalSearchResultsCount: computed(() =>
      store.searchResultsGlobalStore.totalSearchResultsCount(),
    ),
    nlsTraceParent: computed(() => store.filterGlobalStore.nlsTraceParent()),
  })),
  withMethods((store, sessionService = inject(SessionService)) => ({
    toggleCollapsed(): void {
      patchState(store, { collapsed: !store.collapsed() });
    },
    recordFeedbackScore(feedbackScore: number): void {
      patchState(store, { feedbackScore });
    },
    toggleCommentBox(): void {
      patchState(store, { commentBoxOpen: !store.commentBoxOpen() });
    },
    closeCommentBox(): void {
      patchState(store, { commentBoxOpen: false, feedbackComment: '' });
    },
    updateFeedbackComment(feedbackComment: string): void {
      patchState(store, { feedbackComment });
    },
    resetForNewOverview(): void {
      patchState(store, {
        commentBoxOpen: false,
        feedbackComment: '',
        feedbackScore: null,
        collapsed: false,
        uniqueScoreId: generateUUID(),
      });
    },
    fetchAuthenticatedUser: () => {
      sessionService
        .getAuthenticatedUser()
        .pipe(
          takeUntilDestroyed(),
          tap((authenticatedUser) => {
            patchState(store, { authUserId: authenticatedUser?.userId });
          }),
        )
        .subscribe();
    },
  })),
  withHooks((store) => {
    return {
      onInit() {
        store.fetchAuthenticatedUser();
        // The panel stays mounted between consecutive AI searches, so the
        // trace parent changing is the only signal that a new answer arrived.
        effect(() => {
          store.nlsTraceParent();
          untracked(() => store.resetForNewOverview());
        });
      },
    };
  }),
);

export type NlsOverviewAndFeedbackComponentStore = InstanceType<
  typeof NlsOverviewAndFeedbackComponentStore
>;
